import { useState, useEffect } from "react";
import { T } from "../../theme";
import { apiCall, apiCallEx } from "../../apiClient";
import { Card, Badge, SectionTitle, Btn } from "../primitives";

// AI spend — every model call, grouped into the inquiry that caused it.
//
// WHERE IT SITS. Its own screen, Admin ▸ AI & Models ▸ AI usage and costs, as
// of 2026-07-30. It began as a panel underneath the routing configuration and
// grew into a full screen, so reading it meant scrolling past every routing
// control to reach it. Routing is a settings screen you change; this is a
// reporting screen you read. Same permission gates both.
//
// Before either existed, the only surface that could show any of this was the
// operator console, which cannot run on a single-customer estate at all — so
// the data was recorded faithfully and was unreadable by the person paying for
// it.
//
// TWO COST SOURCES, NEVER ADDED TOGETHER. Our own meter (tokens recorded here,
// priced at the rate in force at the time) needs no credential and is always
// present. The provider's billing API is authoritative but needs an account
// admin key. Anthropic showed no cost at all until the meter was added, because
// the only source consulted was the one requiring a key we do not hold.
//
// A BUNDLE IS ONE INQUIRY, not one call. On this estate a single inquiry has
// spanned 52 model calls over ~70 seconds across two models and both the
// primary and the fallback. The bundle row shows the total; expanding it shows
// each call that made up the total.
//
// COST THAT IS NOT KNOWN IS NOT SHOWN AS ZERO. A call whose model has no price
// renders "not priced" and its bundle is flagged partial. platformAdmin.js:2490
// records why that matters: the rollup's COALESCE(price, 0) made "we have no
// price" render identically to "this was free", and every spend figure read
// $0.00 with nothing on screen to say which it was.

type Call = {
  at: string;
  provider: string;
  model: string;
  tier: number;
  role: string;
  input_tokens: number;
  output_tokens: number;
  latency_ms: number | null;
  outcome: string;
  error_class: string | null;
  stop_reason: string | null;
  cost: number | null;
  priced: boolean;
};

type Bundle = {
  request_id: string | null;
  unattributed: boolean;
  calls: Call[];
  call_count: number;
  started_at: string;
  duration_ms: number;
  tiers: number[];
  models: string[];
  used_fallback: boolean;
  used_tertiary: boolean;
  failures: number;
  cost: number;
  cost_is_partial: boolean;
  unpriced_calls: number;
};

type ProviderRow = {
  provider: string;
  display_name: string;
  status: string;
  attribution: string;
  source: string;
  message?: string;
  credential_note?: string;
  per_customer_note?: string;
  // The service returns cost as an OBJECT, not a number:
  //   {"amount":3.63,"currency":"USD","unit":"currency"}
  // and per-model entries keyed `amount`, not `cost`. Getting this wrong is what
  // crashed the whole application: money() called .toFixed on the object, threw,
  // and the error boundary replaced the entire page with "Something went wrong".
  cost?: { amount?: number | null; currency?: string; unit?: string } | null;
  currency?: string;
  input_tokens?: number | null;
  output_tokens?: number | null;
  cost_by_model?: { model: string; amount?: number | null; currency?: string }[];
  // Caveats the adapter attaches to an otherwise successful answer: the period
  // it actually covered when that differs from the one asked for, a token
  // report that failed while the cost report succeeded, a total that is only a
  // floor. Computed and then not rendered is the same as not computed.
  note?: string | null;
  // What OUR OWN records say this provider cost: tokens recorded here, priced
  // at the rate in force when each call was made. Needs no credential, so it is
  // present on every row including the ones with no admin key. Null only when
  // the provider made no calls at all in the window.
  metered?: {
    calls: number;
    input_tokens: number;
    output_tokens: number;
    cost: number | null;
    currency: string;
    unpriced_calls: number;
    is_partial: boolean;
    // The earliest call this platform has any record of for this provider.
    first_seen: string | null;
  } | null;
};
type ProviderUsage = {
  fetched_at: string;
  window: { start: string; end: string; days: number };
  providers: ProviderRow[];
};

type Spend = {
  window_days: number;
  truncated: boolean;
  currency: string;
  totals: { bundles: number; calls: number; cost: number; cost_is_partial: boolean; unpriced_calls: number };
  unpriced_models: string[];
  prices: { provider: string; model: string; input_cost_per_mtok: number; output_cost_per_mtok: number; effective_from: string }[];
  bundles: Bundle[];
};

// Costs here run to small fractions of a cent on a single call, so a 2-decimal
// format would render most of them as $0.00 — the exact ambiguity this panel
// exists to remove. Show enough places to distinguish "very small" from "none".
import { money, amountOf, currencyOf, groupCostByModel } from "./spendFormat";

const ms = (v: number | null) => (v === null ? "—" : v < 1000 ? `${v} ms` : `${(v / 1000).toFixed(1)} s`);
const when = (s: string) => new Date(s).toLocaleString();

// One collapsing control, used for both sections and each provider. The owner's
// note was that the page had become too busy to scroll through: the fix is that
// everything past the headline number is opt-in.
const Caret = ({ open }: { open: boolean }) => (
  <span style={{ display: "inline-block", width: 12, color: T.txD, transform: open ? "rotate(90deg)" : "none", transition: "transform .12s" }}>&#9656;</span>
);

const roleBadge = (role: string) => {
  if (role === "fallback") return <Badge c={T.ylw}>fallback</Badge>;
  if (role === "tertiary") return <Badge c={T.red}>third choice</Badge>;
  return <Badge>first choice</Badge>;
};

export const AiSpendPanel = () => {
  const [data, setData] = useState<Spend | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [days, setDays] = useState(7);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [showPrices, setShowPrices] = useState(false);
  const [pu, setPu] = useState<ProviderUsage | null>(null);
  const [puErr, setPuErr] = useState<string | null>(null);
  const [keyDraft, setKeyDraft] = useState<Record<string, string>>({});
  const [keyBusy, setKeyBusy] = useState<string | null>(null);
  const [keyMsg, setKeyMsg] = useState<Record<string, string>>({});
  const [reload, setReload] = useState(0);
  // Sections start OPEN so the headline figures are never hidden, but every
  // detail beneath them starts closed.
  const [showUsage, setShowUsage] = useState(false);
  const [showProviders, setShowProviders] = useState(true);
  const [openProvider, setOpenProvider] = useState<Record<string, boolean>>({});
  // Opening the request list produced 194 rows in one scroll. Showing the most
  // recent 25 first is not hiding anything — the count and the full list are
  // both one click away — it just stops the default action from being a wall.
  const [showAllBundles, setShowAllBundles] = useState(false);
  const BUNDLE_PREVIEW = 25;

  useEffect(() => {
    let live = true;
    setData(null);
    setErr(null);
    apiCall(`/settings/ai-spend?days=${days}`)
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setErr(e?.message || "Could not load AI spend"); });
    return () => { live = false; };
  }, [days]);

  // The providers' own billing figures. Fetched separately and rendered in its
  // own block: this is authoritative and account-wide, the block above is
  // derived and deployment-local. They are never summed — see the endpoint's
  // comment for why that rule exists.
  useEffect(() => {
    let live = true;
    setPu(null);
    setPuErr(null);
    apiCall(`/settings/provider-usage?days=${days}`)
      .then((d) => { if (live) setPu(d); })
      .catch((e) => { if (live) setPuErr(e?.message || "Could not reach the provider billing APIs"); });
    return () => { live = false; };
  }, [days, reload]);

  // Saving is validated server-side against the provider's billing API before
  // anything is stored, so a key that would leave this panel empty is refused
  // here rather than accepted with a tick.
  const saveKey = async (provider: string) => {
    const key = (keyDraft[provider] || "").trim();
    if (!key) return;
    setKeyBusy(provider);
    setKeyMsg({ ...keyMsg, [provider]: "" });
    try {
      // apiCall returns NULL on any non-2xx — it does not throw. Treating a
      // null as success is why a key the provider REJECTED (two HTTP 400s)
      // rendered "Saved and checked against the provider." in green while
      // nothing was stored. apiCallEx carries the status and the body, so the
      // provider's actual reason can be shown instead of being invented.
      const res = await apiCallEx("/settings/provider-admin-key", {
        method: "PUT",
        body: JSON.stringify({ provider, key }),
      });
      if (!res.ok) {
        const d: any = res.data || {};
        const why = [d.error, d.detail].filter(Boolean).join(" — ")
          || `the server refused it (HTTP ${res.status})`;
        setKeyMsg({ ...keyMsg, [provider]: `Not saved: ${why}` });
        return;
      }
      setKeyDraft({ ...keyDraft, [provider]: "" });
      setKeyMsg({ ...keyMsg, [provider]: "Saved and checked against the provider." });
      setReload((n) => n + 1);
    } catch (e: any) {
      setKeyMsg({ ...keyMsg, [provider]: e?.message || "That key did not work, so it was not saved." });
    } finally {
      setKeyBusy(null);
    }
  };

  if (err) return <Card><div style={{ color: T.red }}>{err}</div></Card>;
  if (!data) return <Card><div style={{ color: T.txD }}>Loading AI spend…</div></Card>;

  const { totals, currency } = data;

  return (
    <Card>
      <SectionTitle
        sub="Every AI call, grouped into the request that caused it. One request can use several models."
        right={
          <div style={{ display: "flex", gap: 6 }}>
            {[1, 7, 30].map((d) => (
              <Btn key={d} v={d === days ? "primary" : "ghost"} sz="sm" onClick={() => setDays(d)}>
                {d === 1 ? "24 hours" : `${d} days`}
              </Btn>
            ))}
          </div>
        }
      >
        AI usage and costs
      </SectionTitle>

      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", margin: "12px 0 4px" }}>
        <div><div style={{ color: T.txD, fontSize: 12 }}>Requests</div><div style={{ fontSize: 20 }}>{totals.bundles}</div></div>
        <div><div style={{ color: T.txD, fontSize: 12 }}>Model calls</div><div style={{ fontSize: 20 }}>{totals.calls}</div></div>
        <div>
          <div style={{ color: T.txD, fontSize: 12 }}>Total cost</div>
          <div style={{ fontSize: 20 }}>
            {money(totals.cost, currency)}
            {totals.cost_is_partial && <span style={{ fontSize: 12, color: T.ylw, marginLeft: 6 }}>at least</span>}
          </div>
        </div>
      </div>

      {totals.cost_is_partial && (
        <div style={{ background: T.ylwS, color: T.ylw, padding: "8px 10px", borderRadius: 6, fontSize: 13, margin: "8px 0" }}>
          {totals.unpriced_calls} call{totals.unpriced_calls === 1 ? " has" : "s have"} no price recorded
          {data.unpriced_models.length > 0 && <> ({data.unpriced_models.join(", ")})</>}, so the real total is higher than shown. It is
          not counted as zero.
        </div>
      )}

      {data.truncated && (
        <div style={{ color: T.txD, fontSize: 12, marginBottom: 8 }}>
          Showing the most recent calls only; older ones in this window are not listed.
        </div>
      )}

      <div style={{ margin: "8px 0" }}>
        <Btn v="ghost" sz="sm" onClick={() => setShowPrices(!showPrices)}>
          {showPrices ? "Hide" : "Show"} the rates these figures use
        </Btn>
        {showPrices && (
          <div style={{ marginTop: 8, fontSize: 13 }}>
            <div style={{ color: T.txD, marginBottom: 6 }}>
              Published list prices per million tokens. These are what the providers charge us, not what a client is billed.
            </div>
            {data.prices.map((p) => (
              <div key={`${p.provider}/${p.model}`} style={{ display: "flex", gap: 12, padding: "2px 0" }}>
                <span style={{ minWidth: 260 }}>{p.provider} / {p.model}</span>
                <span style={{ color: T.txD }}>in {money(p.input_cost_per_mtok, currency)} · out {money(p.output_cost_per_mtok, currency)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Cost by provider ───────────────────────────────────────────────
          MOVED ABOVE the per-request list on 2026-07-30. This is the summary;
          the list below is the detail behind it, and a summary that sits under
          five hundred rows of detail is one nobody reads. Both figures per
          provider are shown, never merged: ours is derived and always present,
          theirs is authoritative and needs a key. Adding them, or showing them
          in one column, would present a derived number as an invoice. */}
      <div style={{ borderTop: `2px solid ${T.brd}`, marginTop: 16, paddingTop: 14 }}>
        <div onClick={() => setShowProviders(!showProviders)} style={{ cursor: "pointer" }}>
          <SectionTitle sub="What each provider cost, by our own meter and by their billing records. The two are shown separately because they do not measure the same thing.">
            <Caret open={showProviders} /> Cost by provider
          </SectionTitle>
        </div>

        {showProviders && puErr && <div style={{ color: T.red, fontSize: 13, padding: "8px 0" }}>{puErr}</div>}
        {showProviders && !pu && !puErr && <div style={{ color: T.txD, fontSize: 13, padding: "8px 0" }}>Asking the providers…</div>}

        {showProviders && pu && (
          <>
            <div style={{ color: T.txD, fontSize: 12, margin: "6px 0 10px" }}>
              Covering {pu.window.start} to {pu.window.end}. These totals are for the whole account, which means every
              customer on the shared key, not just this one. That is a consequence of using one shared key rather than a
              limit of what the providers can tell us.
            </div>

            {pu.providers.map((p) => (
              <div key={p.provider} style={{ borderTop: `1px solid ${T.brd}`, padding: "10px 0" }}>
                {/* Two figures, never merged. OUR METER is arithmetic over the
                    calls this platform made and needs no credential, so it is
                    always present. THE PROVIDER'S figure is what you will be
                    invoiced and is authoritative, but needs an admin key. A
                    missing key now costs the authoritative figure, not every
                    figure — which is why Anthropic showed no cost at all
                    before. */}
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ minWidth: 150, fontWeight: 500 }}>{p.display_name}</span>
                  <span style={{ fontSize: 17 }}>
                    {p.metered && p.metered.cost !== null
                      ? money(p.metered.cost, p.metered.currency)
                      : <span style={{ color: T.txD }}>no calls in this window</span>}
                  </span>
                  <span style={{ color: T.txD, fontSize: 12 }}>
                    by our meter
                    {p.metered?.is_partial && <span style={{ color: T.ylw }}> (at least — {p.metered.unpriced_calls} call(s) have no price on file)</span>}
                  </span>
                  {p.metered && p.metered.calls > 0 && (
                    <span style={{ color: T.txD, fontSize: 12 }}>
                      · {p.metered.calls.toLocaleString()} call{p.metered.calls === 1 ? "" : "s"}
                      , {p.metered.input_tokens.toLocaleString()} in · {p.metered.output_tokens.toLocaleString()} out
                    </span>
                  )}
                </div>

                <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
                  <span style={{ minWidth: 150 }} />
                  {p.status === "ok" ? (
                    <>
                      <span style={{ fontSize: 17 }}>
                        {amountOf(p.cost) === null
                          ? <span style={{ color: T.ylw }}>cost not reported</span>
                          : money(amountOf(p.cost), currencyOf(p.cost, p.currency))}
                      </span>
                      <Badge c={T.grn}>billed by the provider</Badge>
                      {/* The gap between the two figures is the useful part: it
                          is either usage on this account from somewhere other
                          than here, or a call path we are not metering. */}
                      {amountOf(p.cost) !== null && p.metered?.cost != null && (
                        <span style={{ color: T.txD, fontSize: 12 }}>
                          {(() => {
                            // BEFORE explaining a difference, check the two
                            // figures cover the same period. This platform only
                            // began recording AI calls on a particular date; a
                            // provider bill reaching further back is not a
                            // discrepancy, it is a longer measuring tape. The
                            // first version of this line attributed a 30 day
                            // difference to "usage from outside this platform"
                            // when the real cause was that our records were two
                            // days old. Naming a cause you have not established
                            // is worse than naming none.
                            const since = p.metered?.first_seen ? new Date(p.metered.first_seen) : null;
                            // Do the periods actually line up? If our records
                            // start later than the provider's window, the
                            // difference is partly just when we began counting.
                            const shortMeasure = !!since && since.getTime() > new Date(pu.window.start).getTime();
                            const caveat = shortMeasure && since
                              ? `, though our records only begin ${since.toLocaleDateString()} so the two do not cover the same period`
                              : "";
                            const diff = (amountOf(p.cost) as number) - p.metered.cost;
                            if (Math.abs(diff) < 0.005) return `matches our meter${caveat}`;
                            // The cause is only named when the periods match.
                            // Naming one you have not established is worse than
                            // naming none: the first version of this line blamed
                            // a 30 day difference on outside usage when the real
                            // reason was that our records were two days old.
                            const cause = shortMeasure
                              ? ""
                              : (diff > 0
                                ? " — AI spending on this account that this platform did not record"
                                : " — provider billing may lag, or a discount applies");
                            return diff > 0
                              ? `${money(diff, currencyOf(p.cost, p.metered.currency))} more than we metered${caveat}${cause}`
                              : `${money(-diff, currencyOf(p.cost, p.metered.currency))} less than we metered${caveat}${cause}`;
                          })()}
                        </span>
                      )}
                    </>
                  ) : p.status === "metered_only" ? (
                    <>
                      <Badge c={T.txD}>no bill to fetch</Badge>
                      <span style={{ color: T.txD, fontSize: 12 }}>{p.message}</span>
                    </>
                  ) : p.status === "not_configured" ? (
                    <>
                      <Badge c={T.ylw}>provider figure needs a key</Badge>
                      <span style={{ color: T.txD, fontSize: 12 }}>{p.credential_note || p.message}</span>
                    </>
                  ) : (
                    <>
                      <Badge c={T.red}>{p.status}</Badge>
                      <span style={{ color: T.txD, fontSize: 12 }}>{p.message}</span>
                    </>
                  )}
                </div>

                {p.status === "not_configured" && (p.provider === "anthropic" || p.provider === "openai") && (
                  <div style={{ marginTop: 8, marginLeft: 160 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <input
                        type="password"
                        autoComplete="off"
                        placeholder="Paste the account admin key here"
                        value={keyDraft[p.provider] || ""}
                        onChange={(e) => setKeyDraft({ ...keyDraft, [p.provider]: e.target.value })}
                        style={{
                          flex: "1 1 320px", minWidth: 240, padding: "6px 8px", fontSize: 13,
                          border: `1px solid ${T.brd}`, borderRadius: 6, background: T.bg2, color: T.tx,
                        }}
                      />
                      <Btn
                        sz="sm"
                        disabled={keyBusy === p.provider || !(keyDraft[p.provider] || "").trim()}
                        onClick={() => saveKey(p.provider)}
                      >
                        {keyBusy === p.provider ? "Checking…" : "Save and check"}
                      </Btn>
                    </div>
                    <div style={{ color: T.txD, fontSize: 11.5, marginTop: 4 }}>
                      This is checked against the provider before it is saved, so a key that would not work is refused
                      rather than stored. It is encrypted and never shown again.
                    </div>
                    {keyMsg[p.provider] && (
                      <div style={{ fontSize: 12, marginTop: 4, color: keyMsg[p.provider].startsWith("Saved") ? T.grn : T.red }}>
                        {keyMsg[p.provider]}
                      </div>
                    )}
                  </div>
                )}

                {/* A caveat on a figure belongs beside the figure. Kept out of
                    the badge row so it wraps as prose rather than squeezing the
                    numbers. */}
                {p.status === "ok" && p.note && (
                  <div style={{ marginLeft: 160, marginTop: 4, color: T.ylw, fontSize: 12 }}>
                    {p.note}
                  </div>
                )}

                {(() => {
                  const { models, kinds } = groupCostByModel(p.cost_by_model);
                  if (models.length === 0) return null;
                  const isOpen = !!openProvider[p.provider];
                  const ccy = currencyOf(p.cost, p.currency);
                  return (
                    <div style={{ marginTop: 6, marginLeft: 160 }}>
                      <span
                        onClick={() => setOpenProvider({ ...openProvider, [p.provider]: !isOpen })}
                        style={{ cursor: "pointer", color: T.txD, fontSize: 12, userSelect: "none" }}
                      >
                        <Caret open={isOpen} /> {isOpen ? "Hide" : "Show"} the {models.length} model{models.length === 1 ? "" : "s"} behind this
                      </span>
                      {isOpen && (
                        <div style={{ overflowX: "auto", marginTop: 6 }}>
                          {/* One row per model with a column per billing category,
                              most expensive first. The provider sends these as 89
                              flat "model, direction" strings; rendered literally
                              that is an unreadable wall, which is what the owner
                              saw. */}
                          <table style={{ borderCollapse: "collapse", fontSize: 12, minWidth: 420 }}>
                            <thead>
                              <tr style={{ color: T.txD, textAlign: "left" }}>
                                <th style={{ padding: "3px 10px 3px 0" }}>Model</th>
                                {kinds.map((k) => (
                                  <th key={k} style={{ padding: "3px 10px", textAlign: "right" }}>{k}</th>
                                ))}
                                <th style={{ padding: "3px 0 3px 10px", textAlign: "right" }}>Model total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {models.map((m) => (
                                <tr key={m.model} style={{ borderTop: `1px solid ${T.brd}` }}>
                                  <td style={{ padding: "3px 10px 3px 0", whiteSpace: "nowrap" }}>{m.model}</td>
                                  {kinds.map((k) => (
                                    <td key={k} style={{ padding: "3px 10px", textAlign: "right", color: m.kinds[k] ? undefined : T.txD }}>
                                      {m.kinds[k] ? money(m.kinds[k], ccy) : "—"}
                                    </td>
                                  ))}
                                  <td style={{ padding: "3px 0 3px 10px", textAlign: "right", fontWeight: 500 }}>{money(m.total, ccy)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            ))}

            <div style={{ color: T.txD, fontSize: 12, marginTop: 10 }}>
              Every provider above has a figure, because our own meter needs no permission from anyone. What a missing
              account level key costs you is the provider's own confirmation of it, not the figure itself.
              <br /><br />
              {/* Stated as a standing property rather than a list of features,
                  which would be out of date the week after it was written. */}
              Our meter counts calls made through AI Routing. Other parts of the product call a provider directly —
              document search has to turn text into numbers before it can search it, and the Negotiation tab has its
              own model chooser — and those calls appear on the provider's bill without appearing here. That is the
              usual reason the provider's figure is the larger of the two. Compare the two over a period our records
              actually cover, or the difference is only telling you when we started counting.
            </div>
          </>
        )}
      </div>
      {/* ── Every request, and the calls inside it ─────────────────────────
          The long one. Collapsed by default: it is the evidence behind the
          summary above, not the thing you open the screen to read. */}
      <div style={{ borderTop: `2px solid ${T.brd}`, marginTop: 16, paddingTop: 14 }}>
        <div onClick={() => setShowUsage(!showUsage)} style={{ cursor: "pointer" }}>
          <SectionTitle sub="Each inquiry, the model calls it made, whether it fell back to a second or third model, and what each one cost.">
            <Caret open={showUsage} /> Every request in detail
            <span style={{ color: T.txD, fontWeight: 400, fontSize: 13 }}> ({data.bundles.length})</span>
          </SectionTitle>
        </div>
      </div>

      {showUsage && data.bundles.length === 0 && <div style={{ color: T.txD, padding: "12px 0" }}>No AI calls in this period.</div>}

      {showUsage && (showAllBundles ? data.bundles : data.bundles.slice(0, BUNDLE_PREVIEW)).map((b) => {
        const key = b.request_id || b.started_at;
        const isOpen = !!open[key];
        return (
          <div key={key} style={{ borderTop: `1px solid ${T.brd}`, padding: "10px 0" }}>
            <div
              onClick={() => setOpen({ ...open, [key]: !isOpen })}
              style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", flexWrap: "wrap" }}
            >
              <span style={{ color: T.txD, fontSize: 12, minWidth: 150 }}>{when(b.started_at)}</span>
              <span style={{ minWidth: 90 }}>
                {money(b.cost, currency)}
                {b.cost_is_partial && <span style={{ color: T.ylw, fontSize: 11, marginLeft: 4 }}>+?</span>}
              </span>
              <span style={{ color: T.txD, fontSize: 12 }}>
                {b.call_count} call{b.call_count === 1 ? "" : "s"} · {ms(b.duration_ms)}
              </span>
              <span style={{ fontSize: 12 }}>{b.models.join(", ")}</span>
              {b.tiers.map((t) => <Badge key={t}>tier {t}</Badge>)}
              {b.used_fallback && <Badge c={T.ylw}>used fallback</Badge>}
              {b.used_tertiary && <Badge c={T.red}>used third choice</Badge>}
              {b.failures > 0 && <Badge c={T.red}>{b.failures} failed</Badge>}
              {b.unattributed && <Badge c={T.txD}>not linked to a request</Badge>}
              <span style={{ marginLeft: "auto", color: T.txD, fontSize: 12 }}>{isOpen ? "hide calls" : "show calls"}</span>
            </div>

            {isOpen && (
              <div style={{ marginTop: 8, overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
                  <thead>
                    <tr style={{ color: T.txD, textAlign: "left" }}>
                      <th style={{ padding: "4px 8px" }}>Time</th>
                      <th style={{ padding: "4px 8px" }}>Model</th>
                      <th style={{ padding: "4px 8px" }}>Tier</th>
                      <th style={{ padding: "4px 8px" }}>Which choice</th>
                      <th style={{ padding: "4px 8px" }}>In</th>
                      <th style={{ padding: "4px 8px" }}>Out</th>
                      <th style={{ padding: "4px 8px" }}>Took</th>
                      <th style={{ padding: "4px 8px" }}>Result</th>
                      <th style={{ padding: "4px 8px" }}>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {b.calls.map((c, i) => (
                      <tr key={i} style={{ borderTop: `1px solid ${T.brd}` }}>
                        <td style={{ padding: "4px 8px", whiteSpace: "nowrap" }}>{new Date(c.at).toLocaleTimeString()}</td>
                        <td style={{ padding: "4px 8px" }}>{c.provider} / {c.model}</td>
                        <td style={{ padding: "4px 8px" }}>{c.tier}</td>
                        <td style={{ padding: "4px 8px" }}>{roleBadge(c.role)}</td>
                        <td style={{ padding: "4px 8px" }}>{c.input_tokens.toLocaleString()}</td>
                        <td style={{ padding: "4px 8px" }}>{c.output_tokens.toLocaleString()}</td>
                        <td style={{ padding: "4px 8px" }}>{ms(c.latency_ms)}</td>
                        <td style={{ padding: "4px 8px" }}>
                          {c.outcome === "success"
                            ? <span style={{ color: T.txD }}>ok</span>
                            : <span style={{ color: T.red }}>{c.error_class || c.outcome}</span>}
                          {c.stop_reason && c.stop_reason !== "end_turn" && (
                            <span style={{ color: T.ylw, marginLeft: 6 }}>{c.stop_reason}</span>
                          )}
                        </td>
                        <td style={{ padding: "4px 8px", color: c.priced ? undefined : T.ylw }}>
                          {money(c.cost, currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}


      {/* Never a silent truncation. If rows are being withheld the screen says
          how many and offers all of them — a list that quietly stops at 25
          reads as "that is everything", which is the one thing it is not. */}
      {showUsage && !showAllBundles && data.bundles.length > BUNDLE_PREVIEW && (
        <div style={{ borderTop: `1px solid ${T.brd}`, padding: "12px 0" }}>
          <Btn v="ghost" sz="sm" onClick={() => setShowAllBundles(true)}>
            Showing the {BUNDLE_PREVIEW} most recent of {data.bundles.length} — show all
          </Btn>
        </div>
      )}

    </Card>
  );
};
